import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  List,
  ListItemButton,
  TextField,
  Typography,
} from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import ContextMenu, {
  type ContextMenuItem,
} from '../../../../shared/components/ui/ContextMenu';
import { TableCheckbox } from '../../../../shared/components/table/atoms/TableCheckbox';
import { useFolderContents } from '../../hooks/useFolderContents';
import { useSitePanelState } from '../../hooks/useSitePanelState';
import { useFileSelectionStore } from '../../stores/fileSelectionStore';
import { useFolderUpload } from '../../hooks/useFolderUpload';
import { collectInputFiles, getDropEntries } from '../../utils/folderUpload';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { BILLDA_MOVE_MIME } from '../../hooks/useDragAndDrop';
import { useDeleteFile } from '../../hooks/useDeleteFile';
import {
  useRenameFolder,
  useRenameFile,
  useDeleteFolder,
} from '../../hooks/useFolderMutations';
import { useFileDownload } from '../../hooks/useFileDownload';
import type { FolderDto, SiteFileDto } from '../../api/filesApi';
import { getFileIcon } from './fileIcons';
import { formatFileSize } from './formatFileSize';

interface FolderBrowserProps {
  siteId: number;
  folderId?: string;
  onNavigate: (folderId?: string) => void;
  /** Navigate up from site root (e.g. back to site list). */
  onNavigateUp?: () => void;
  canWrite: boolean;
  uploadButtonRef?: React.RefObject<HTMLInputElement | null>;
  /** Ref to the hidden folder <input>, so the parent can trigger folder selection. */
  folderInputRef?: React.RefObject<HTMLInputElement | null>;
  /** Hide the internal breadcrumb when the parent page renders its own. */
  hideBreadcrumb?: boolean;
  /** Called when the user chooses "New folder" from the background context menu. */
  onCreateFolder?: () => void;
}

type MenuTarget =
  | { type: 'folder'; item: FolderDto }
  | { type: 'file'; item: SiteFileDto }
  | { type: 'background' };

export function FolderBrowser({
  siteId,
  folderId,
  onNavigate,
  onNavigateUp,
  canWrite,
  uploadButtonRef,
  folderInputRef,
  hideBreadcrumb = false,
  onCreateFolder,
}: FolderBrowserProps) {
  const { data, isLoading } = useFolderContents(siteId, folderId);
  const { openPanel } = useSitePanelState();
  const { upload, uploadEntries, isPreparing } = useFolderUpload(
    siteId,
    folderId
  );
  const internalRef = useRef<HTMLInputElement>(null);
  const fileInputRef = uploadButtonRef ?? internalRef;
  const internalFolderRef = useRef<HTMLInputElement>(null);
  const folderInputEl = folderInputRef ?? internalFolderRef;

  const [isFileDropOver, setIsFileDropOver] = useState(false);
  const [renameTarget, setRenameTarget] = useState<{
    id: string;
    type: 'file' | 'folder';
    name: string;
  } | null>(null);
  const [renameName, setRenameName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    type: 'file' | 'folder';
    name: string;
  } | null>(null);

  const deleteFile = useDeleteFile(siteId);
  const deleteFolder = useDeleteFolder(siteId);
  const renameFolderMutation = useRenameFolder(siteId);
  const renameFileMutation = useRenameFile();
  const downloadFile = useFileDownload();

  const {
    toggle,
    toggleRange,
    selectAll,
    clearAll,
    isSelected,
    selectedItems,
    lastSelectedIndex,
  } = useFileSelectionStore();

  const { getDragProps, getDropProps, isDragOver } = useDragAndDrop(
    siteId,
    folderId ?? null
  );

  // Clear selection on folder navigation
  useEffect(() => {
    clearAll();
  }, [folderId, clearAll]);

  // Prevent browser default file-drop behavior
  useEffect(() => {
    const prevent = (e: DragEvent) => e.preventDefault();
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, []);

  // File / folder upload handling. collectInputFiles reads webkitRelativePath,
  // so the same handler serves both the plain file input and the folder input.
  const handleFileInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files || event.target.files.length === 0) {
        return;
      }
      const files = Array.from(event.target.files);
      event.target.value = '';
      void upload(collectInputFiles(files));
    },
    [upload]
  );

  // Drag-and-drop file upload (OS files, not in-app moves)
  const handleFileDragEnter = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (canWrite && !event.dataTransfer.types.includes(BILLDA_MOVE_MIME)) {
        setIsFileDropOver(true);
      }
    },
    [canWrite]
  );

  const handleFileDragOver = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (canWrite && !event.dataTransfer.types.includes(BILLDA_MOVE_MIME)) {
        setIsFileDropOver(true);
      }
    },
    [canWrite]
  );

  const handleFileDragLeave = useCallback((event: React.DragEvent) => {
    const relatedTarget = event.relatedTarget as Node | null;
    const currentTarget = event.currentTarget as Node;
    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      setIsFileDropOver(false);
    }
  }, []);

  const handleFileDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsFileDropOver(false);
      // Only handle OS file/folder drops, not in-app moves.
      if (event.dataTransfer.types.includes(BILLDA_MOVE_MIME)) {
        return;
      }
      if (!canWrite) {
        return;
      }
      // Snapshot entries synchronously — the DataTransfer is cleared on return.
      const entries = getDropEntries(event.dataTransfer);
      if (entries.length > 0) {
        void uploadEntries(entries);
      } else if (event.dataTransfer.files.length > 0) {
        void upload(collectInputFiles(event.dataTransfer.files));
      }
    },
    [canWrite, upload, uploadEntries]
  );

  // Build a flat list of all items for shift+click range selection
  const allItems = useMemo(() => {
    if (!data) {
      return [];
    }
    const folders = data.folders.map(f => ({
      id: f.id,
      type: 'folder' as const,
    }));
    const files = data.files.map(f => ({ id: f.id, type: 'file' as const }));
    return [...folders, ...files];
  }, [data]);

  const allSelected =
    allItems.length > 0 && selectedItems.size === allItems.length;
  const someSelected = selectedItems.size > 0 && !allSelected;

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      clearAll();
    } else {
      selectAll(allItems);
    }
  }, [allSelected, clearAll, selectAll, allItems]);

  const handleCheckboxClick = useCallback(
    (
      event: React.MouseEvent,
      id: string,
      type: 'file' | 'folder',
      index: number
    ) => {
      event.stopPropagation();
      if (event.shiftKey && lastSelectedIndex !== null) {
        toggleRange(lastSelectedIndex, index, allItems);
      } else {
        toggle(id, type);
        useFileSelectionStore.setState({ lastSelectedIndex: index });
      }
    },
    [lastSelectedIndex, toggleRange, toggle, allItems]
  );

  const handleRename = useCallback(() => {
    const trimmed = renameName.trim();
    if (!trimmed || !renameTarget) {
      return;
    }
    if (renameTarget.type === 'folder') {
      renameFolderMutation.mutate(
        { folderId: renameTarget.id, name: trimmed },
        { onSuccess: () => setRenameTarget(null) }
      );
    } else {
      renameFileMutation.mutate(
        { fileId: renameTarget.id, name: trimmed },
        { onSuccess: () => setRenameTarget(null) }
      );
    }
  }, [renameName, renameTarget, renameFolderMutation, renameFileMutation]);

  const [menuState, setMenuState] = useState<{
    open: boolean;
    target: MenuTarget | null;
    anchorPosition?: { top: number; left: number };
    anchorEl?: HTMLElement | null;
  }>({ open: false, target: null });

  const handleContextMenu = useCallback(
    (event: React.MouseEvent, target: MenuTarget) => {
      event.preventDefault();
      event.stopPropagation();
      setMenuState({
        open: true,
        target,
        anchorPosition: { top: event.clientY, left: event.clientX },
        anchorEl: undefined,
      });
    },
    []
  );

  const handleOverflowClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, target: MenuTarget) => {
      event.stopPropagation();
      setMenuState({
        open: true,
        target,
        anchorEl: event.currentTarget,
        anchorPosition: undefined,
      });
    },
    []
  );

  const closeMenu = useCallback(() => {
    setMenuState({ open: false, target: null });
  }, []);

  // Top-level right-click handler for the folder browser.
  // Handles two scenarios:
  //   1. Normal right-click (no menu open) — show the background menu.
  //   2. Portal right-click (menu already open, event bubbles through React's
  //      portal tree) — peek under the backdrop with elementsFromPoint and
  //      switch to the appropriate context or close.
  const handleBoxContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const boxEl = e.currentTarget;
      const isPortalEvent = !boxEl.contains(e.target as Node);

      if (isPortalEvent) {
        e.preventDefault();
        e.stopPropagation();

        const elements = document.elementsFromPoint(e.clientX, e.clientY);
        const realTarget = elements.find(el => boxEl.contains(el)) as
          | HTMLElement
          | undefined;

        if (!realTarget) {
          closeMenu();
          return;
        }

        const folderRow = realTarget.closest('[data-testid^="folder-row-"]');
        if (folderRow && data) {
          const id = folderRow
            .getAttribute('data-testid')!
            .replace('folder-row-', '');
          const folder = data.folders.find(f => f.id === id);
          if (folder) {
            handleContextMenu(e, { type: 'folder', item: folder });
            return;
          }
        }

        const fileRow = realTarget.closest('[data-testid^="file-row-"]');
        if (fileRow && data) {
          const id = fileRow
            .getAttribute('data-testid')!
            .replace('file-row-', '');
          const file = data.files.find(f => f.id === id);
          if (file) {
            handleContextMenu(e, { type: 'file', item: file });
            return;
          }
        }

        handleContextMenu(e, { type: 'background' });
        return;
      }

      handleContextMenu(e, { type: 'background' });
    },
    [data, handleContextMenu, closeMenu]
  );

  const getMenuItems = useCallback((): ContextMenuItem[] => {
    if (!menuState.target) {
      return [];
    }

    if (menuState.target.type === 'background') {
      const items: ContextMenuItem[] = [];
      if (canWrite && onCreateFolder) {
        items.push({
          key: 'new-folder',
          label: 'New folder',
          icon: <CreateNewFolderOutlinedIcon fontSize="small" />,
          onClick: onCreateFolder,
        });
      }
      if (canWrite) {
        items.push(
          {
            key: 'upload',
            label: 'Upload files',
            icon: <UploadFileIcon fontSize="small" />,
            onClick: () => fileInputRef.current?.click(),
          },
          {
            key: 'upload-folder',
            label: 'Upload folder',
            icon: <DriveFolderUploadOutlinedIcon fontSize="small" />,
            onClick: () => folderInputEl.current?.click(),
          }
        );
      }
      return items;
    }

    if (menuState.target.type === 'folder') {
      const folder = menuState.target.item as FolderDto;
      const items: ContextMenuItem[] = [
        {
          key: 'open',
          label: 'Open',
          onClick: () => onNavigate(folder.id),
        },
      ];
      if (canWrite) {
        items.push(
          {
            key: 'rename',
            label: 'Rename',
            onClick: () => {
              setRenameTarget({
                id: folder.id,
                type: 'folder',
                name: folder.name,
              });
              setRenameName(folder.name);
            },
          },
          {
            key: 'delete',
            label: 'Delete',
            onClick: () =>
              setDeleteTarget({
                id: folder.id,
                type: 'folder',
                name: folder.name,
              }),
          }
        );
      }
      return items;
    }

    // File menu
    const file = menuState.target.item as SiteFileDto;
    const items: ContextMenuItem[] = [
      {
        key: 'view-details',
        label: 'View details',
        onClick: () => openPanel('file', file.id),
      },
    ];
    if (canWrite) {
      items.push(
        {
          key: 'rename',
          label: 'Rename',
          onClick: () => {
            setRenameTarget({ id: file.id, type: 'file', name: file.name });
            setRenameName(file.name);
          },
        },
        {
          key: 'download',
          label: 'Download',
          dividerAfter: true,
          onClick: () => downloadFile.mutate(file.id),
        },
        {
          key: 'delete',
          label: 'Delete',
          onClick: () =>
            setDeleteTarget({ id: file.id, type: 'file', name: file.name }),
        }
      );
    } else {
      items.push({
        key: 'download',
        label: 'Download',
        onClick: () => downloadFile.mutate(file.id),
      });
    }
    return items;
  }, [
    menuState.target,
    canWrite,
    onNavigate,
    openPanel,
    deleteFile,
    deleteFolder,
    downloadFile,
    onCreateFolder,
    fileInputRef,
    folderInputEl,
  ]);

  if (isLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', py: 6 }}
        data-testid="folder-browser-loading"
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Box
      data-testid="folder-browser"
      onDragEnter={handleFileDragEnter}
      onDragOver={handleFileDragOver}
      onDragLeave={handleFileDragLeave}
      onDrop={handleFileDrop}
      onContextMenu={handleBoxContextMenu}
      sx={{
        position: 'relative',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInput}
        accept=".pdf,.txt,.rtf,.png,.jpg,.jpeg,.heic,.heif,.webp,.tiff,.tif,.doc,.docx,.odt,.csv,.xls,.xlsx,.ods,.pptx,.odp,.zip"
        style={{ display: 'none' }}
        data-testid="file-input"
        multiple
      />

      {/* Hidden folder input — selects a whole directory tree */}
      <input
        type="file"
        ref={folderInputEl}
        onChange={handleFileInput}
        style={{ display: 'none' }}
        data-testid="folder-input"
        webkitdirectory=""
        multiple
      />

      {/* Preparing overlay while the folder hierarchy is being created */}
      {isPreparing && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 11,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            bgcolor: 'action.hover',
          }}
          data-testid="folder-upload-preparing"
        >
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Preparing upload…
          </Typography>
        </Box>
      )}

      {/* Drop overlay for OS file uploads */}
      {isFileDropOver && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
            border: 2,
            borderStyle: 'dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
          }}
        >
          <Typography variant="body1" color="primary">
            Drop files or folders here to upload
          </Typography>
        </Box>
      )}

      {/* Breadcrumb — show full path including root when inside a sub-folder */}
      {!hideBreadcrumb && data.breadcrumb.length > 1 && (
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 1 }}>
          <Breadcrumbs data-testid="folder-breadcrumb">
            {data.breadcrumb.map((item, index) => {
              const isLast = index === data.breadcrumb.length - 1;
              if (isLast) {
                return (
                  <Typography
                    key={item.id ?? 'root'}
                    variant="body2"
                    color="text.primary"
                  >
                    {item.name}
                  </Typography>
                );
              }
              return (
                <Link
                  key={item.id ?? 'root'}
                  component="button"
                  variant="body2"
                  underline="hover"
                  onClick={() => onNavigate(item.id ?? undefined)}
                  {...getDropProps(item.id)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: isDragOver(item.id)
                      ? 'action.hover'
                      : 'transparent',
                    borderRadius: 1,
                    px: 0.5,
                    transition: 'background-color 0.15s',
                  }}
                >
                  {item.name}
                </Link>
              );
            })}
          </Breadcrumbs>
        </Box>
      )}

      {/* Parent directory row with select-all checkbox */}
      <List disablePadding>
        {(() => {
          if (folderId && data.breadcrumb.length >= 2) {
            const parentItem = data.breadcrumb[data.breadcrumb.length - 2];
            const parentId = parentItem.id;
            return (
              <ListItemButton
                onClick={() => onNavigate(parentId ?? undefined)}
                {...getDropProps(parentId)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1,
                  px: { xs: 2, sm: 3 },
                  borderBottom: '1px solid',
                  borderColor: isDragOver(parentId)
                    ? 'primary.main'
                    : 'divider',
                  bgcolor: isDragOver(parentId)
                    ? 'action.hover'
                    : 'transparent',
                  transition: 'background-color 0.15s, border-color 0.15s',
                }}
                data-testid="parent-folder-row"
              >
                {allItems.length > 0 && (
                  <Box
                    onClick={e => {
                      e.stopPropagation();
                      handleSelectAll();
                    }}
                    data-testid="select-all-checkbox"
                  >
                    <TableCheckbox
                      checked={allSelected}
                      indeterminate={someSelected}
                    />
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary">
                  ..
                </Typography>
              </ListItemButton>
            );
          }
          if (!onNavigateUp) {
            return null;
          }
          return (
            <ListItemButton
              onClick={onNavigateUp}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1,
                px: { xs: 2, sm: 3 },
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
              data-testid="parent-folder-row"
            >
              {allItems.length > 0 && (
                <Box
                  onClick={e => {
                    e.stopPropagation();
                    handleSelectAll();
                  }}
                  data-testid="select-all-checkbox"
                >
                  <TableCheckbox
                    checked={allSelected}
                    indeterminate={someSelected}
                  />
                </Box>
              )}
              <Typography variant="body2" color="text.secondary">
                ..
              </Typography>
            </ListItemButton>
          );
        })()}
      </List>

      {/* Content */}
      {data.folders.length === 0 && data.files.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 6,
            gap: 1,
          }}
          data-testid="folder-empty-state"
        >
          <InsertDriveFileOutlinedIcon
            sx={{ fontSize: 48, color: 'text.disabled' }}
          />
          <Typography variant="body2" color="text.secondary">
            This folder is empty
          </Typography>
          {canWrite && (
            <>
              <Typography variant="caption" color="text.secondary">
                Upload files or create a folder to get started
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<UploadFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="empty-upload-button"
                >
                  Upload a file
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DriveFolderUploadOutlinedIcon />}
                  onClick={() => folderInputEl.current?.click()}
                  data-testid="empty-upload-folder-button"
                >
                  Upload folder
                </Button>
              </Box>
            </>
          )}
        </Box>
      ) : (
        <List disablePadding>
          {/* Folder rows */}
          {data.folders.map((folder, idx) => (
            <ListItemButton
              key={folder.id}
              onClick={() => onNavigate(folder.id)}
              onContextMenu={e =>
                handleContextMenu(e, { type: 'folder', item: folder })
              }
              {...getDragProps({ id: folder.id, type: 'folder' })}
              {...getDropProps(folder.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.5,
                px: { xs: 2, sm: 3 },
                borderBottom: '1px solid',
                borderColor: isDragOver(folder.id) ? 'primary.main' : 'divider',
                bgcolor: isDragOver(folder.id) ? 'action.hover' : 'transparent',
                transition: 'background-color 0.15s, border-color 0.15s',
              }}
              data-testid={`folder-row-${folder.id}`}
            >
              <Box
                onClick={e => handleCheckboxClick(e, folder.id, 'folder', idx)}
                data-testid={`row-checkbox-${folder.id}`}
              >
                <TableCheckbox checked={isSelected(folder.id)} />
              </Box>
              <FolderOutlinedIcon
                sx={{ fontSize: 20, color: 'action.active' }}
              />
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {folder.name}
              </Typography>
              <IconButton
                size="small"
                onClick={e =>
                  handleOverflowClick(e, { type: 'folder', item: folder })
                }
                data-testid={`row-actions-button-${folder.id}`}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </ListItemButton>
          ))}

          {/* File rows */}
          {data.files.map((file, idx) => (
            <ListItemButton
              key={file.id}
              onClick={() => openPanel('file', file.id)}
              onContextMenu={e =>
                handleContextMenu(e, { type: 'file', item: file })
              }
              {...getDragProps({ id: file.id, type: 'file' })}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.5,
                px: { xs: 2, sm: 3 },
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
              data-testid={`file-row-${file.id}`}
            >
              <Box
                onClick={e =>
                  handleCheckboxClick(
                    e,
                    file.id,
                    'file',
                    data.folders.length + idx
                  )
                }
                data-testid={`row-checkbox-${file.id}`}
              >
                <TableCheckbox checked={isSelected(file.id)} />
              </Box>
              {getFileIcon(file.contentType, 16)}
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {file.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flexShrink: 0, display: { xs: 'none', sm: 'block' } }}
              >
                {formatFileSize(file.fileSizeBytes)}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flexShrink: 0 }}
              >
                {new Date(file.uploadedAt).toLocaleDateString()}
              </Typography>
              <IconButton
                size="small"
                onClick={e =>
                  handleOverflowClick(e, { type: 'file', item: file })
                }
                data-testid={`row-actions-button-${file.id}`}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </ListItemButton>
          ))}
        </List>
      )}

      {/* Context menu */}
      <ContextMenu
        open={menuState.open}
        onClose={closeMenu}
        anchorPosition={menuState.anchorPosition}
        anchorEl={menuState.anchorEl}
        items={getMenuItems()}
      />

      {/* Rename dialog */}
      <Dialog
        open={renameTarget !== null}
        onClose={() => setRenameTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename {renameTarget?.type}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={renameName}
            onChange={e => setRenameName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleRename();
              }
            }}
            sx={{ mt: 1 }}
            data-testid="rename-input"
            slotProps={{ htmlInput: { maxLength: 255 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRename}
            disabled={
              !renameName.trim() || renameName.trim() === renameTarget?.name
            }
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        data-testid="delete-confirm-dialog"
      >
        <DialogTitle>Delete {deleteTarget?.type}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {`"${deleteTarget?.name}" will be moved to the recycle bin.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteTarget(null)}
            data-testid="delete-cancel-button"
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (deleteTarget) {
                if (deleteTarget.type === 'folder') {
                  deleteFolder.mutate(deleteTarget.id);
                } else {
                  deleteFile.mutate(deleteTarget.id);
                }
                setDeleteTarget(null);
              }
            }}
            data-testid="delete-confirm-button"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
